import React, { useState, useRef, useEffect } from 'react';
import { X, Send, User } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getSessionId = () => {
  let sid = localStorage.getItem('chat_session_id');
  if (!sid) {
    sid = 'chat-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('chat_session_id', sid);
  }
  return sid;
};

const BotIcon = ({ size = 'w-5 h-5' }) => (
  <span className={size} role="img" aria-label="bot" style={{ fontSize: size === 'w-8 h-8' ? '28px' : '20px', lineHeight: 1 }}>
    🤖
  </span>
);

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('chat_dismissed') === '1');
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'שלום! אני העוזר הדיגיטלי של בריאות הגבר 🤖\nאיך אוכל לעזור לך?' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        message: userMsg,
        session_id: sessionId.current
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      const isBudget = detail.toLowerCase().includes('budget');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: isBudget 
          ? 'הצ\'אט הדיגיטלי שלנו לא זמין כרגע. צור קשר איתנו בוואטסאפ ונשמח לעזור! 💬' 
          : 'מצטער, אירעה שגיאה. נסה שוב בעוד כמה שניות.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat Toggle Button - Right side with animated robot */}
      {!isOpen && !dismissed && (
        <div className="fixed bottom-6 right-6 z-50 max-lg:bottom-20" data-testid="chat-widget-wrapper">
          <button
            onClick={() => { setDismissed(true); sessionStorage.setItem('chat_dismissed', '1'); }}
            className="absolute -top-1.5 -left-1.5 w-5 h-5 bg-stone-400 hover:bg-red-500 text-white rounded-full flex items-center justify-center text-xs leading-none z-10 transition-colors shadow"
            data-testid="chat-dismiss-btn"
          >
            ×
          </button>
          <button
            onClick={() => setIsOpen(true)}
            data-testid="chat-toggle-btn"
            className="w-14 h-14 bg-[#2C3E2D] hover:bg-[#1a2a1b] text-white rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110"
            style={{
              boxShadow: '0 0 0 3px rgba(255,255,255,0.8), 0 4px 20px rgba(44, 62, 45, 0.4)'
            }}
          >
          <span className="animate-look-around" style={{ fontSize: '28px', lineHeight: 1, display: 'inline-block' }}>🤖</span>
        </button>
        </div>
      )}

      {/* Chat Window - Smaller */}
      {isOpen && (
        <div
          data-testid="chat-window"
          className="fixed bottom-4 right-4 z-50 max-lg:bottom-20 w-[320px] max-w-[calc(100vw-32px)] h-[440px] max-h-[calc(100vh-100px)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-stone-200"
          style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.15)' }}
        >
          {/* Header */}
          <div className="bg-[#2C3E2D] text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
            <button
              onClick={() => setIsOpen(false)}
              data-testid="chat-close-btn"
              className="p-1 hover:bg-white/10 rounded-full transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2 text-right">
              <div>
                <p className="font-bold text-sm" style={{ fontFamily: 'Heebo, sans-serif' }}>בריאות הגבר</p>
                <p className="text-xs text-stone-300">עוזר דיגיטלי 🤖</p>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2.5 bg-stone-50" data-testid="chat-messages">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                  msg.role === 'user' ? 'bg-[#D97706]' : 'bg-[#2C3E2D]'
                } text-white`}>
                  {msg.role === 'user' ? <User className="w-3.5 h-3.5" /> : <span style={{ fontSize: '14px' }}>🤖</span>}
                </div>
                <div
                  data-testid={`chat-msg-${i}`}
                  className={`max-w-[78%] px-3 py-2 rounded-2xl text-[13px] leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-[#D97706] text-white rounded-tr-sm'
                      : 'bg-white text-stone-700 border border-stone-200 rounded-tl-sm shadow-sm'
                  }`}
                  style={{ fontFamily: 'Heebo, sans-serif', direction: 'rtl' }}
                  // HTML-escape the raw message BEFORE applying the two
                  // safe transformations (line breaks + URL linkification).
                  // Without this a bot reply containing "<script>" would
                  // execute; now angle-brackets are converted to entities
                  // first, so the only HTML that reaches the DOM is the
                  // <br/> and <a> we generate ourselves.
                  dangerouslySetInnerHTML={{
                    __html: String(msg.content ?? '')
                      .replace(/&/g, '&amp;')
                      .replace(/</g, '&lt;')
                      .replace(/>/g, '&gt;')
                      .replace(/"/g, '&quot;')
                      .replace(/'/g, '&#39;')
                      .replace(/\n/g, '<br/>')
                      .replace(
                        /(https?:\/\/[^\s<]+)/g,
                        '<a href="$1" target="_blank" rel="noopener noreferrer" style="color: #D97706; font-weight: bold; text-decoration: underline;">לחצו כאן לצפייה</a>'
                      )
                  }}
                />
              </div>
            ))}
            {loading && (
              <div className="flex gap-2 items-start">
                <div className="w-7 h-7 rounded-full bg-[#2C3E2D] flex items-center justify-center flex-shrink-0 text-white">
                  <span style={{ fontSize: '14px' }}>🤖</span>
                </div>
                <div className="bg-white border border-stone-200 px-3 py-2.5 rounded-2xl rounded-tl-sm shadow-sm">
                  <div className="flex gap-1.5">
                    <div className="w-1.5 h-1.5 bg-stone-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-1.5 h-1.5 bg-stone-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-1.5 h-1.5 bg-stone-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-3 py-2.5 bg-white border-t border-stone-200 flex-shrink-0">
            <div className="flex items-center gap-2">
              <button
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                data-testid="chat-send-btn"
                className="w-9 h-9 bg-[#D97706] hover:bg-[#B45309] disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-full flex items-center justify-center transition-colors flex-shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="שאל אותי כל שאלה..."
                data-testid="chat-input"
                className="flex-1 h-9 px-3 bg-stone-100 border-0 rounded-full text-[13px] text-right outline-none focus:ring-2 focus:ring-[#D97706]/30 placeholder:text-stone-400"
                style={{ fontFamily: 'Heebo, sans-serif', direction: 'rtl' }}
                disabled={loading}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
